import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';

interface Agent {
  id: number;
  deleted_at: string | null;
  editable: boolean;
  status: string;
}

const MoreActionsButton: FC<{
  agent: Agent;
  onEditClick: (agentId: number) => void;
  onDeleteClick: (agentId: number) => void;
  onInitializeClick: (agentId: number) => void;
  onApplyModificationsClick: (agentId: number) => void;
  onDisableClick: (agentId: number) => void;
}> = ({
  agent,
  onEditClick,
  onDeleteClick,
  onInitializeClick,
  onApplyModificationsClick,
  onDisableClick,
}) => {
  const { formatMessage } = useIntl();

  const handleEditClick = useCallback(() => {
    onEditClick(agent.id);
  }, [agent.id, onEditClick]);

  const handleDeleteClick = useCallback(() => {
    onDeleteClick(agent.id);
  }, [agent.id, onDeleteClick]);

  const handleInitializeClick = useCallback(() => {
    onInitializeClick(agent.id);
  }, [agent.id, onInitializeClick]);

  const handleApplyModificationsClick = useCallback(() => {
    onApplyModificationsClick(agent.id);
  }, [agent.id, onApplyModificationsClick]);

  const handleDisableClick = useCallback(() => {
    onDisableClick(agent.id);
  }, [agent.id, onDisableClick]);

  const isDeleted = !!agent.deleted_at;
  const isEditable = agent.editable;
  const canInitialize = agent.status === 'D' && isEditable; // Draft status
  const canApplyModifications = agent.status === 'M' && isEditable; // Pending Changes status
  const canDisable =
    (agent.status === 'P' || agent.status === 'M') && isEditable; // Published or Pending Changes

  return (
    <div className="d-flex gap-1">
      {isEditable && (
        <Button
          size="sm"
          color="white"
          onClick={handleEditClick}
          title={formatMessage({
            id: 'app.views.ai.agents.agents_admin_admin.more_actions.edit',
            defaultMessage: 'Edit this agent',
          })}
        >
          <i className="fe fe-edit" />
        </Button>
      )}
      {canInitialize && (
        <Button
          size="sm"
          color="white"
          onClick={handleInitializeClick}
          title={formatMessage({
            id: 'app.views.ai.agents.agents_admin_admin.more_actions.initialize',
            defaultMessage: 'Initialize this agent',
          })}
        >
          <i className="fe fe-play" />
        </Button>
      )}
      {canApplyModifications && (
        <Button
          size="sm"
          color="white"
          onClick={handleApplyModificationsClick}
          title={formatMessage({
            id: 'app.views.ai.agents.agents_admin_admin.more_actions.apply_modifications',
            defaultMessage: 'Apply Modifications',
          })}
        >
          <i className="fe fe-refresh-cw" />
        </Button>
      )}
      {canDisable && (
        <Button
          size="sm"
          color="white"
          onClick={handleDisableClick}
          title={formatMessage({
            id: 'app.views.ai.agents.agents_admin_admin.more_actions.disable',
            defaultMessage: 'Disable this agent',
          })}
        >
          <i className="fe fe-pause-circle" />
        </Button>
      )}
      {isEditable && !isDeleted && (
        <Button
          size="sm"
          color="white"
          onClick={handleDeleteClick}
          title={formatMessage({
            id: 'app.views.ai.agents.agents_admin_admin.more_actions.delete',
            defaultMessage: 'Delete this agent',
          })}
        >
          <i className="fe fe-trash" />
        </Button>
      )}
    </div>
  );
};

export default React.memo(MoreActionsButton);
